import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../../context/AuthContext';
import { theme } from '../../../theme';
import { useAuthProfileRetrieveQuery, useAuthProfileUpdateMutation } from '../../../services/GLOWClient';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Profile = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const fileInputRef = useRef(null);

  const { data: userProfile, isLoading } = useAuthProfileRetrieveQuery(undefined, {
    skip: !isAuthenticated(),
  });

  const [updateProfile, { isLoading: isUpdating }] = useAuthProfileUpdateMutation();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    profile_photo: null,
  });

  // Mettre à jour formData quand userProfile est chargé
  useEffect(() => {
    if (userProfile) {
      setFormData({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        email: userProfile.email || '',
        phone: userProfile.phone || '',
        profile_photo: null,
      });
      setPreviewPhoto(userProfile.profile_photo || null);
    }
  }, [userProfile]);

  // Nettoyer les URLs blob
  useEffect(() => {
    return () => {
      if (previewPhoto && previewPhoto.startsWith('blob:')) {
        URL.revokeObjectURL(previewPhoto);
      }
    };
  }, [previewPhoto]);

  const profile = userProfile || user;

  const handleEditToggle = () => {
    if (isEditing) {
      // Réinitialiser
      if (userProfile) {
        setFormData({
          first_name: userProfile.first_name || '',
          last_name: userProfile.last_name || '',
          email: userProfile.email || '',
          phone: userProfile.phone || '',
          profile_photo: null,
        });
        setPreviewPhoto(userProfile.profile_photo || null);
      }
      setSuccessMessage('');
      setErrorMessage('');
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'profile_photo' && files && files[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, profile_photo: file }));

      if (previewPhoto && previewPhoto.startsWith('blob:')) {
        URL.revokeObjectURL(previewPhoto);
      }
      setPreviewPhoto(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    try {
      setSuccessMessage('');
      setErrorMessage('');

      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          data.append(key, value);
        }
      });

      const result = await updateProfile({ userRequest: data }).unwrap();
      updateUser(result);
      setPreviewPhoto(result.profile_photo || null);
      setSuccessMessage(t('pro_profile.update_success'));
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrorMessage(
        extractErrorMessage(error, t('pro_profile.update_error'))
      );
    }
  };

  const handleDeleteAccount = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmation !== 'SUPPRIMER') {
      setErrorMessage(t('pro_profile.delete.confirmation_required'));
      return;
    }

    setDeleteLoading(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.delete('/api/auth/delete-account/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: {
          email: user.email,
          password: 'authenticated_user' // Indicateur pour le backend
        }
      });

      if (response.status === 200) {
        // Clear local UI state IMMEDIATELY
        updateUser(null);

        // Clear tokens and user data from storage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        
        // Reset RTK Query caches
        try {
          const { store } = require('../../../store');
          const { GLOWApiClient, dashboardApi, salonsApi } = require('../../../services/GLOWClient');
          store.dispatch(GLOWApiClient.util.resetApiState());
          store.dispatch(dashboardApi.util.resetApiState());
          store.dispatch(salonsApi.util.resetApiState());
        } catch (e) {
          console.warn('Failed to reset API caches on delete account', e);
        }

        // Rediriger vers la landing page en bypassant la redirection automatique
        window.location.href = '/?bypass=true';
      }
    } catch (error) {
      setErrorMessage(t('pro_profile.delete.error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile) {
    return <Alert severity="error">{t('pro_profile.load_error')}</Alert>;
  }

  return (
    <Box sx={{ py: 0 }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%)`,
          color: 'white',
          py: { xs: 3, md: 6 },
          px: 3,
          mb: 4,
          borderRadius: '0 0 24px 24px',
          boxShadow: `0 8px 32px rgba(65, 101, 72, 0.15)`,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', md: '3rem' } }}>
            {t('pro_profile.title')}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
            {t('pro_profile.manage_personal_info')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>
        {/* Header Card */}
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
            mb: 4,
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.secondary[50]} 100%)`,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              {/* Avatar */}
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                  src={previewPhoto || undefined}
                  sx={{
                    width: { xs: 80, md: 120 },
                    height: { xs: 80, md: 120 },
                    background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.secondary[500]} 100%)`,
                    fontSize: { xs: '2rem', md: '3rem' },
                    fontWeight: 800,
                    boxShadow: `0 12px 32px rgba(65, 101, 72, 0.25)`,
                    border: `4px solid white`,
                  }}
                >
                  {(!previewPhoto && !profile.profile_photo) &&
                    `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`}
                </Avatar>

                {isEditing && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: theme.colors.primary[500],
                      borderRadius: '50%',
                      p: 1,
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                    component="label"
                  >
                    <EditIcon sx={{ color: 'white', fontSize: 20 }} />
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="profile_photo"
                      accept="image/*"
                      hidden
                      onChange={handleChange}
                    />
                  </Box>
                )}
              </Box>

              {/* Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5, fontSize: { xs: '1.25rem', md: '2.125rem' } }}>
                  {profile.first_name} {profile.last_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Chip
                    label={profile.role === 'professional_employe' ? t('pro_profile.employee') : t('pro_profile.manager')}
                    sx={{
                      backgroundColor: theme.colors.primary[100],
                      color: theme.colors.primary[700],
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                    }}
                  />
                  {profile.owned_salons?.length > 0 && (
                    <Chip
                      label={profile.owned_salons.length === 1
                        ? t('pro_profile.salon_count_one', { count: profile.owned_salons.length })
                        : t('pro_profile.salon_count_other', { count: profile.owned_salons.length })}
                      sx={{
                        backgroundColor: theme.colors.secondary[100],
                        color: theme.colors.secondary[700],
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                      }}
                    />
                  )}
                </Box>
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                  {profile.email}
                </Typography>
              </Box>

              {/* Bouton Modifier */}
              <Button
                variant="contained"
                onClick={handleEditToggle}
                sx={{
                  background: isEditing
                    ? theme.colors.error[500]
                    : `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.secondary[600]} 100%)`,
                  color: 'white',
                  fontWeight: 700,
                  borderRadius: { xs: '50%', md: '20px' },
                  minWidth: { xs: 40, md: 'auto' },
                  width: { xs: 40, md: 'auto' },
                  height: { xs: 40, md: 'auto' },
                  px: { xs: 0, md: 2.5 },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: 0, md: 1 },
                  '&:hover': {
                    background: isEditing ? theme.colors.error[600] : undefined,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                {isEditing ? <CancelIcon /> : <EditIcon />}
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' } }}>
                  {isEditing ? t('pro_profile.cancel') : t('pro_profile.edit')}
                </Box>
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Informations personnelles */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                border: `1px solid ${theme.colors.primary[100]}`,
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                {/* Container pour titre et boutons */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2 }}>
                  {/* Boutons d'édition - au-dessus du titre sur mobile, à droite sur desktop */}
                  {isEditing && (
                    <Box sx={{ display: 'flex', gap: 1, mb: { xs: 2, md: 0 }, order: { xs: -1, md: 1 }, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
                      <Button
                        variant="outlined"
                        onClick={handleEditToggle}
                        sx={{
                          borderColor: theme.colors.error[300],
                          color: theme.colors.error[600],
                          minWidth: { xs: 40, md: 'auto' },
                          width: { xs: 40, md: 'auto' },
                          height: { xs: 40, md: 'auto' },
                          borderRadius: { xs: '50%', md: 1 },
                          px: { xs: 0, md: 2 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: { xs: 0, md: 1 },
                          '&:hover': {
                            borderColor: theme.colors.error[500],
                            backgroundColor: theme.colors.error[50],
                          },
                        }}
                      >
                        <CancelIcon />
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                          {t('pro_profile.cancel')}
                        </Box>
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isUpdating}
                        sx={{
                          backgroundColor: theme.colors.success[500],
                          minWidth: { xs: 40, md: 'auto' },
                          width: { xs: 40, md: 'auto' },
                          height: { xs: 40, md: 'auto' },
                          borderRadius: { xs: '50%', md: 1 },
                          px: { xs: 0, md: 2 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: { xs: 0, md: 1 },
                          '&:hover': { backgroundColor: theme.colors.success[600] },
                          '&:disabled': { backgroundColor: theme.colors.success[300] },
                        }}
                      >
                        {isUpdating ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                          {isUpdating ? t('pro_profile.saving') : t('pro_profile.save')}
                        </Box>
                      </Button>
                    </Box>
                  )}

                  {/* Titre */}
                  <Typography variant="h6" sx={{ fontWeight: 700, order: { xs: 1, md: 0 } }}>
                    {t('pro_profile.personal_info')}
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
                {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

                {isEditing ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ color: theme.colors.primary[500], mr: 1, fontSize: 20 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.colors.text.secondary }}>
                          {t('pro_profile.fields.first_name')}
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        size="small"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ color: theme.colors.primary[500], mr: 1, fontSize: 20 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.colors.text.secondary }}>
                          {t('pro_profile.fields.last_name')}
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        size="small"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon sx={{ color: theme.colors.primary[500], mr: 1, fontSize: 20 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.colors.text.secondary }}>
                          {t('pro_profile.fields.email')}
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        size="small"
                        required
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon sx={{ color: theme.colors.primary[500], mr: 1, fontSize: 20 }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.colors.text.secondary }}>
                          {t('pro_profile.fields.phone')}
                        </Typography>
                      </Box>

                      <TextField
                        fullWidth
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        size="small"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={3}>
                    {/* Prénom - 6 colonnes mobile, 3 colonnes desktop */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 2, backgroundColor: theme.colors.primary[50], borderRadius: 2 }}>
                        <PersonIcon sx={{ color: theme.colors.primary[500], fontSize: 24 }} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.colors.text.secondary, display: 'block' }}>
                            {t('pro_profile.fields.first_name')}
                          </Typography>

                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>
                            {profile.first_name}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Nom - 6 colonnes mobile, 3 colonnes desktop */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 2, backgroundColor: theme.colors.primary[50], borderRadius: 2 }}>
                        <PersonIcon sx={{ color: theme.colors.primary[500], fontSize: 24 }} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.colors.text.secondary, display: 'block' }}>
                            {t('pro_profile.fields.last_name')}
                          </Typography>

                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>
                            {profile.last_name}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Email - toute la largeur mobile, 3 colonnes desktop */}
                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 2, backgroundColor: theme.colors.primary[50], borderRadius: 2 }}>
                        <EmailIcon sx={{ color: theme.colors.primary[500], fontSize: 24 }} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.colors.text.secondary, display: 'block' }}>
                            {t('pro_profile.fields.email')}
                          </Typography>

                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.text.primary, wordBreak: 'break-word' }}>
                            {profile.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Téléphone - toute la largeur mobile, 3 colonnes desktop */}
                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, p: 2, backgroundColor: theme.colors.primary[50], borderRadius: 2 }}>
                        <PhoneIcon sx={{ color: theme.colors.primary[500], fontSize: 24 }} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, color: theme.colors.text.secondary, display: 'block' }}>
                            {t('pro_profile.fields.phone')}
                          </Typography>

                          <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>
                            {profile.phone || t('pro_profile.not_provided')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {isEditing && (
                  <Alert severity="info" sx={{ mt: 3 }}>
                    {t('pro_profile.save_notice')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Section Supprimer mon compte */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            border: '1px solid #fca5a5',
            mt: 3,
            bgcolor: '#fef2f2',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#dc2626' }}>
              {t('pro_profile.delete.title')}
            </Typography>
            <Divider sx={{ mb: 3, borderColor: '#fecaca' }} />
            
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              {t('pro_profile.delete.description')}
            </Typography>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteAccount}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {t('pro_profile.delete.button')}
            </Button>
          </CardContent>
        </Card>

        {/* Fenêtre de confirmation de suppression */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{t('pro_profile.delete.dialog_title')}</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              {t('pro_profile.delete.dialog_warning')}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('pro_profile.delete.dialog_instruction')} <strong>SUPPRIMER</strong>.
            </Typography>
            
            <TextField
              fullWidth
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder={t('pro_profile.delete.placeholder')}
              variant="outlined"
              margin="normal"
              error={errorMessage.includes('SUPPRIMER')}
            />
            
            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDeleteDialog(false);
              setDeleteConfirmation('');
              setErrorMessage('');
            }}>
              {t('pro_profile.cancel')}
            </Button>
            <Button 
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleteLoading || deleteConfirmation !== 'SUPPRIMER'}
              startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
            >
              {deleteLoading ? t('pro_profile.delete.deleting') : t('pro_profile.delete.confirm_button')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Profile;